import React from 'react';

/**
 * Base skeleton primitives with shimmer animation
 * Used to build layout-specific skeleton screens
 */

// Shimmer animation styles (inline to avoid SCSS dependency)
const shimmerKeyframes = `
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
`;

// Inject keyframes once
if (typeof document !== 'undefined') {
  const styleId = 'skeleton-shimmer-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = shimmerKeyframes;
    document.head.appendChild(style);
  }
}

const shimmerStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s ease-in-out infinite',
};

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Basic rectangular skeleton block
 */
export const SkeletonBlock = ({
  className = '',
  style,
  width,
  height = '1rem',
}: SkeletonProps & { width?: string; height?: string }) => (
  <div
    className={`rounded ${className}`}
    style={{
      ...shimmerStyle,
      width: width || '100%',
      height,
      ...style,
    }}
  />
);

/**
 * Text line skeleton - simulates a line of text
 */
export const SkeletonText = ({
  className = '',
  style,
  width = '100%',
  lines = 1,
}: SkeletonProps & { width?: string; lines?: number }) => (
  <div className={`space-y-2 ${className}`} style={style}>
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className="rounded h-4"
        style={{
          ...shimmerStyle,
          width: i === lines - 1 && lines > 1 ? '70%' : width,
        }}
      />
    ))}
  </div>
);

/**
 * Circular skeleton - for avatars, icons
 */
export const SkeletonCircle = ({
  className = '',
  style,
  size = '2.5rem',
}: SkeletonProps & { size?: string }) => (
  <div
    className={`rounded-full ${className}`}
    style={{
      ...shimmerStyle,
      width: size,
      height: size,
      flexShrink: 0,
      ...style,
    }}
  />
);

/**
 * Card skeleton - rectangular with padding appearance
 */
export const SkeletonCard = ({
  className = '',
  style,
  children,
}: SkeletonProps & { children?: React.ReactNode }) => (
  <div
    className={`rounded-lg border border-gray-200 bg-white p-4 ${className}`}
    style={style}
  >
    {children || (
      <div className="space-y-3">
        <SkeletonBlock height="1.25rem" width="60%" />
        <SkeletonText lines={2} />
        <SkeletonBlock height="0.875rem" width="40%" />
      </div>
    )}
  </div>
);

/**
 * Navigation item skeleton - for sidebar menu items
 */
export const SkeletonNavItem = ({
  className = '',
  style,
  showIcon = true,
}: SkeletonProps & { showIcon?: boolean }) => (
  <div className={`flex items-center gap-3 px-3 py-2 ${className}`} style={style}>
    {showIcon && <SkeletonCircle size="1.25rem" />}
    <SkeletonBlock height="0.875rem" width="70%" />
  </div>
);

/**
 * Button skeleton
 */
export const SkeletonButton = ({
  className = '',
  style,
  width = '6rem',
  height = '2.25rem',
}: SkeletonProps & { width?: string; height?: string }) => (
  <div
    className={`rounded-md ${className}`}
    style={{
      ...shimmerStyle,
      width,
      height,
      ...style,
    }}
  />
);

/**
 * Image placeholder skeleton
 */
export const SkeletonImage = ({
  className = '',
  style,
  aspectRatio = '16/9',
}: SkeletonProps & { aspectRatio?: string }) => (
  <div
    className={`rounded-lg w-full ${className}`}
    style={{
      ...shimmerStyle,
      aspectRatio,
      ...style,
    }}
  />
);

export default {
  Block: SkeletonBlock,
  Text: SkeletonText,
  Circle: SkeletonCircle,
  Card: SkeletonCard,
  NavItem: SkeletonNavItem,
  Button: SkeletonButton,
  Image: SkeletonImage,
};
